import type { AffectUser } from '@/types/affectUser';
import type { CancellationReason } from '@/ui/components/AppointmentAttendanceStatus/types';

import type { AppointmentAttendanceFeedback } from './appointmentAttendanceFeedback';
import type { DrugScreen } from './drugScreen';
import type { ExternalLink, PersonMinimal, Staff } from './person';

export const ScheduledBySource = {
  SELF_SCHEDULED_LINK: 'Self-Scheduled (Link)',
  SELF_SCHEDULED_APP: 'Self-Scheduled (App)',
  SELF_SCHEDULED_CHAT: 'Self-Scheduled (Chat)',
  AFFECT_STAFF: 'Scheduled by Staff',
  SCHEDULING_AGENT: 'Scheduled by Agent',
  GROUP_APPOINTMENT_MANAGEMENT: 'Scheduled by Group Appointment Management'
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type ScheduledBySource = (typeof ScheduledBySource)[keyof typeof ScheduledBySource];

export const ChartingStatus = {
  SESSION_COMPLETE: 'session_complete',
  SESSION_INCOMPLETE: 'session_incomplete',
  CHART_PENDING: 'chart_pending'
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type ChartingStatus = (typeof ChartingStatus)[keyof typeof ChartingStatus];

export type AppointmentAttendanceDrugScreen = Pick<
  DrugScreen,
  'is_compliant' | 'occurred_at' | 'kind' | 'result' | 'id' | 'conducted_by'
>;

// The treatment group membership that put a member on a group appointment. Both
// fields are null for memberships that predate enrollment attribution.
export interface GroupEnrollment {
  enrolled_at: string | null;
  enrolled_by: AffectUser | null;
}

export interface AppointmentAttendance {
  appointment: Appointment;
  appointment_within_availability: boolean | null;
  attendance_taken_at?: string;
  auto_cancel_eligible: boolean;
  auto_cancelled_at?: string;
  cancelled_by_member_at?: string;
  cancellation_reason?: CancellationReason | null;
  charting_completed: boolean;
  charting_status?: ChartingStatus;
  confirmed_by_member_at?: string;
  drug_screen?: AppointmentAttendanceDrugScreen | null;
  feedback?: AppointmentAttendanceFeedback[];
  group_enrollment?: GroupEnrollment | null;
  id: string;
  internal_status: string;
  member_id?: string;
  person: PersonMinimal | null;
  state: string;
  _appointment_type_config: object | null;
}

export interface AppointmentExternalLink {
  zoom_start?: ExternalLink;
  zoom_join?: ExternalLink;
  homebase?: ExternalLink;
}
export interface AppointmentVideoSession {
  type: 'member' | 'provider';
  participant: string;
  join_at: string | null;
  leave_at: string | null;
  leave_at_estimated: boolean;
  duration_display: string | null;
}

export interface Appointment {
  created_at: string;
  description?: string | null;
  drug_screen_expected?: boolean | null;
  duration_in_minutes: number | null;
  end_at?: string;
  external_links?: AppointmentExternalLink;
  healthie_id: number | null;
  id?: string;
  initiator: AffectUser | null;
  meeting_status?: string | null;
  providers: Staff[];
  participants?: PersonMinimal[];
  recurring_appointment: RecurringAppointment | null;
  rescheduled_by_source: string | null;
  rescheduled_by: AffectUser | null;
  last_rescheduled_at: string | null;
  scheduled_by: AffectUser | null;
  start_at?: string;
  title?: string;
  zoom_join_url: string | null;
  scheduled_by_source: ScheduledBySource;
  video_sessions?: AppointmentVideoSession[];
}

export interface RecurringAppointment {
  healthie_id: number | null;
  id: string;
  repeat_interval: string;
  repeat_times: string;
}

export interface SyncUserAppointment {
  appointment_healthie_id: string | null;
  source?: string | null;
}

export interface SyncUserAppointmentResponse {
  appointment_id: string | null;
}
